import SimpleTablePage from "../common/SimpleTablePage";
import { date2locale } from "../common/common";
import { visitTypes } from "./NewMedicalVisit";
import { DatePicker, Select, Tag } from "antd";
import dayjs from "dayjs";
import DebounceSelect from "../common/DebounceSelect";
import { api } from "../common/api";
import { Link, useNavigate } from "react-router";
import { UploadOutlined } from "@ant-design/icons";

export const states = {
  PROGRAMMED: "Programmata",
  COMPLETED: "Completata",
  CANCELED: "Annullata",
};

export const outcomes = {
  FIT: "Idoneo",
  FIT_WITH_RESTRICTIONS: "Idoneo con restrizioni",
  FIT_WITH_PRESCRIPTIONS: "Idoneo con prescrizioni",
  TEMPORARILY_UNFIT: "Non idoneo temporaneamente",
  PERMANENTLY_UNFIT: "Non idoneo permanente",
};
const columns = [
  {
    key: "id",
    title: "ID",
    width: 70,
    dataIndex: "id",
    sorter: true,
    ellipsis: true,
    render: (id) => <Link to={`${id}`}>{id}</Link>,
  },
  {
    key: "date",
    title: "Data",
    dataIndex: "date",
    sorter: true,
    render: date2locale,
    ellipsis: true,
  },
  {
    key: "employee",
    title: "Dipendente",
    dataIndex: "employee",
    render: (employee) => employee?.fullName,
    sorter: true,
    ellipsis: true,
  },
  {
    key: "doctor",
    title: "Medico competente",
    dataIndex: "doctor",
    sorter: true,
    render: (x) => x.firstName + " " + x.lastName,
    ellipsis: true,
  },
  {
    key: "type",
    title: "Tipologia visita",
    dataIndex: "type",
    sorter: true,
    render: (x) => visitTypes[x],
    ellipsis: true,
  },
  {
    key: "state",
    title: "Stato",
    dataIndex: "state",
    sorter: true,
    render: (x) => <Tag>{states[x]}</Tag>,
    ellipsis: true,
  },
  {
    key: "outcome",
    title: "Giudizio d'idoneità",
    dataIndex: "outcome",
    sorter: true,
    ellipsis: true,
    render: (_, record) => outcomes[record.medicalFitness?.outcome] ?? "-",
  },
  {
    key: "revoked",
    title: "Giudizio d'idoneità revocato",
    ellipsis: true,
    dataIndex: "revoked",
    sorter: true,
    render: (_, record) => (record.medicalFitness?.revokeDate ? "Sì" : "-"),
  },
];

const entityFilter = (name, label, resource, getLabel) => ({
  name,
  label,
  readableValue: (value) => value && JSON.parse(value).label,
  formItem: {
    props: {
      getValueProps: (value) => ({ value: value && JSON.parse(value) }),
      normalize: (value) => value && JSON.stringify(value),
    },
    children: (
      <DebounceSelect
        allowClear
        minSize={0}
        fetchOptions={(query) =>
          api[resource]
            .list({
              [resource === "doctor" ? "name" : "query"]: query,
              pageSize: 9999,
            })
            .then(({ data }) => data.map((item) => ({ label: getLabel(item), value: item.id })))
        }
      />
    ),
  },
});

const selectFilter = (name, label, values) => ({
  name,
  label,
  readableValue: (value) => values[value] ?? value,
  formItem: {
    children: (
      <Select
        allowClear
        options={Object.entries(values).map(([value, label]) => ({ value, label }))}
      />
    ),
  },
});

const dateFilter = (name, label) => ({
  name,
  label,
  readableValue: date2locale,
  formItem: {
    props: {
      getValueProps: (value) => ({ value: value ? dayjs(value) : null }),
      normalize: (value) => value?.format("YYYY-MM-DD"),
    },
    children: <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />,
  },
});

const filterItems = [
  entityFilter("employee", "Dipendente", "employee", (item) => item.fullName),
  entityFilter(
    "doctor",
    "Medico competente",
    "doctor",
    (item) => `${item.firstName} ${item.lastName}`,
  ),
  selectFilter("type", "Tipologia visita", visitTypes),
  selectFilter("state", "Stato", states),
  selectFilter("outcome", "Giudizio d'idoneità", outcomes),
  selectFilter("revoked", "Giudizio d'idoneità revocato", { true: "Sì", false: "No" }),
  dateFilter("startDate", "Data dal"),
  dateFilter("endDate", "Data al"),
];

const fetchVisits = (params) =>
  api.medicalVisit.list({
    ...params,
    employee: params.employee ? JSON.parse(params.employee).value : undefined,
    doctor: params.doctor ? JSON.parse(params.doctor).value : undefined,
  });

export default function MedicalVisits() {
  const navigate = useNavigate();
  return (
    <SimpleTablePage
      actionsMenu={{
        items: (record) =>
          [
            record.state === "PROGRAMMED" && {
              key: "uploadMedicalFitness",
              label: "Carica giudizio d'idoneità",
              icon: <UploadOutlined />,
            },
          ].filter((x) => !!x),
        onClick: (key, record) =>
          key === "uploadMedicalFitness" && navigate(`${record.id}/uploadMedicalFitness`),
      }}
      title={"Visite mediche"}
      resource="medicalVisit"
      columns={columns}
      filterItems={filterItems}
      fetchFunction={fetchVisits}
    />
  );
}
